import React, { useDeferredValue, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { css } from "../../../styled-system/css";
import { type ColumnDef, getCoreRowModel, getExpandedRowModel, useReactTable } from "@tanstack/react-table";
import { type ManagedTableState, type ManagedTableStateSetters } from "components/table/usePersistedTableState";
import { Flex, HStack, VStack, Wrap } from "../../../styled-system/jsx";
import { RT8ColumnSelector } from "components/core/blocks/MetricSelector";
import { SearchInput } from "components/fields/base/TextField";
import { RT8Pagination } from "components/table/Pagination";
import { Table } from "components/table/Table";
import { resetTable, selectionStateToSelectedIds } from "components/table/utils";
import { RT8FilterWidget } from "views/merchandise/tables/FilterWidget";
import { Button } from "components/core/basic/Button";
import { MdRotateLeft } from "react-icons/md";
import { DownloadV4Report } from "../perf_reports/DownloadReport";
import ExpanderContainer from "components/core/basic/ExpanderContainer";
import { DevErrorDisplay } from "components/core/blocks/DevErrorDisplay";
import { type OrgQueryV4Row, useGetV4OrgReportByOrg } from "../perf_reports/api/organization/v4OrgReportByOrg";
import { useV4OrgReportExport } from "../perf_reports/api/organization/v4OrgReportExport";
import OrgPerformanceExpander from "views/merchandise/OrgPerformanceExpander";
import BulkTagsForm from "components/core/blocks/BulkTagsForm";
import { BulkActionButton } from "components/table/BulkActionButton";
import Switch from "react-bootstrap-switch";
import { useBulkUpdateOrgProperties } from "../perf_reports/api/organization/useBulkUpdateOrgProperties";
import { useGetV4OrgReportOptions } from "../perf_reports/api/organization/v4OrgReportOptions";
import { AGENCY_DASHBOARD_STAT_CHANNELS } from "./AgencyDashboard";

const AgencyOrgReportTable = ({
  columns,
  state,
  initialState,
  setters,
  selectedMarketplace,
  showHiddenOrgs,
  setShowHiddenOrgs,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<OrgQueryV4Row, any>[];
  state: ManagedTableState;
  initialState: ManagedTableState;
  setters: ManagedTableStateSetters;
  selectedMarketplace: string;
  showHiddenOrgs: boolean;
  setShowHiddenOrgs: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [filledTags, setFilledTags] = useState<string[]>([]);

  const deferredSearch = useDeferredValue(state.globalFilter);
  const queryClient = useQueryClient();

  const filters = useMemo(() => {
    const hiddenOrgsFilter =
      showHiddenOrgs ? [] : [{ id: "org_is_visible", value: { op: "=", value: !showHiddenOrgs } }];
    const orgChannelFilter = [
      {
        id: "org_type",
        value: {
          op: "anyof",
          value: AGENCY_DASHBOARD_STAT_CHANNELS,
        },
      },
    ];

    return [...orgChannelFilter, ...state.columnFilters, ...hiddenOrgsFilter];
  }, [state.columnFilters, showHiddenOrgs]);

  const byOrgOptionsGetter = useGetV4OrgReportByOrg(selectedMarketplace);
  const orgByOrgReportQuery = useQuery(
    byOrgOptionsGetter({
      pagination: state.pagination,
      sorting: state.sorting,
      filters: filters,
      search: deferredSearch,
    }),
  );

  const exportMutation = useV4OrgReportExport(filters, selectedMarketplace);
  const orgReportOptionsQuery = useQuery(useGetV4OrgReportOptions());

  const tagOptions = useMemo(
    () => [
      { value: "all", label: "All Tags" },
      ...(orgReportOptionsQuery.data?.data.tags.map((tag) => {
        return {
          value: tag,
          label: tag,
        };
      }) ?? []),
    ],
    [orgReportOptionsQuery.data?.data.tags],
  );

  void queryClient.prefetchQuery({
    ...byOrgOptionsGetter({
      pagination: { pageIndex: state.pagination.pageIndex + 1, pageSize: state.pagination.pageSize },
      sorting: state.sorting,
      filters: state.columnFilters,
      search: deferredSearch,
    }),
    staleTime: 10000,
  });

  const table = useReactTable({
    data: orgByOrgReportQuery.data?.data || [],
    columns,
    defaultColumn: {
      filterFn: () => true,
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    getRowId: (row) => row.org_id.toString(),
    enableRowSelection: true,
    rowCount: orgByOrgReportQuery.data?.count || 0,
    initialState: initialState,
    state: state,
    ...setters,
    manualPagination: true,
    meta: {
      showSkeleton:
        orgByOrgReportQuery.isFetching && (!orgByOrgReportQuery.data || orgByOrgReportQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        tags: tagOptions,
        channel: [
          {
            value: "AMAZON",
            label: "Amazon",
          },
          {
            value: "WALMART",
            label: "Walmart",
          },
          {
            value: "SHOPIFY",
            label: "Shopify",
          },
        ],
      },
    },
  });

  const bulkUpdateOrgPropertiesMutation = useBulkUpdateOrgProperties();
  const selected = selectionStateToSelectedIds(table.getState().rowSelection);

  if (orgByOrgReportQuery.isError) {
    return <DevErrorDisplay userMessage={"Error loading stats"} error={orgByOrgReportQuery.error} />;
  }

  return (
    <div>
      <Wrap justifyContent={"end"} alignItems={"center"} width={"100%"} padding={"small"} fontSize={"large"}>
        <BulkActionButton
          selected={selected}
          options={[
            {
              label: "Add Organization Tags",
              action: () => {
                bulkUpdateOrgPropertiesMutation.mutate(
                  {
                    org_ids: selected.map((id) => parseInt(id)),
                    updates: [
                      {
                        field: "tags",
                        action: "add",
                        value: filledTags,
                      },
                    ],
                  },
                  { onSuccess: () => setFilledTags([]) },
                );
              },
              actionFormFields: (
                <BulkTagsForm
                  filledTags={filledTags}
                  setFilledTags={setFilledTags}
                  selected={selected}
                  suggestedTags={tagOptions.filter((t) => t.value !== "all").map((tag) => tag.value)}
                  taggedItemDescription={"organization"}
                  actionType={"add"}
                />
              ),
              confirmationMessage: (
                <p>
                  Are you sure you want to add {filledTags.length > 1 ? "these tags" : "this tag"} to the selected
                  organization
                  {selected.length > 1 ? "s" : ""}?
                </p>
              ),
              disableConfirmButton: !filledTags.length,
            },
            {
              label: "Remove Organization Tags",
              action: () => {
                bulkUpdateOrgPropertiesMutation.mutate(
                  {
                    org_ids: selected.map((id) => parseInt(id)),
                    updates: [
                      {
                        field: "tags",
                        action: "remove",
                        value: filledTags,
                      },
                    ],
                  },
                  { onSuccess: () => setFilledTags([]) },
                );
              },
              actionFormFields: (
                <BulkTagsForm
                  filledTags={filledTags}
                  setFilledTags={setFilledTags}
                  selected={selected}
                  suggestedTags={tagOptions.filter((t) => t.value !== "all").map((tag) => tag.value)}
                  taggedItemDescription={"organization"}
                  actionType={"remove"}
                />
              ),
              disableConfirmButton: !filledTags.length,
              confirmationMessage: (
                <p>
                  Are you sure you want to remove {filledTags.length > 1 ? "these tags" : "this tag"} from the selected
                  organization
                  {selected.length > 1 ? "s" : ""}?
                </p>
              ),
            },
            {
              label: `Hide Brand${selected.length > 1 ? "s" : ""}`,
              action: () => {
                bulkUpdateOrgPropertiesMutation.mutate({
                  org_ids: selected.map((id) => parseInt(id)),
                  updates: [
                    {
                      field: "visibility",
                      action: "set",
                      value: {
                        visibility: "hide",
                        marketplace: selectedMarketplace,
                      },
                    },
                  ],
                });
              },
              confirmationMessage: (
                <>
                  <p>
                    Are you sure you want to hide {selected.length} {selected.length > 1 ? "brands" : "brand"}?
                  </p>
                  <p>You can un-hide them at anytime by clicking the "Show Hidden Brands"</p>
                </>
              ),
            },
            {
              label: `Show Brand${selected.length > 1 ? "s" : ""}`,
              action: () => {
                bulkUpdateOrgPropertiesMutation.mutate({
                  org_ids: selected.map((id) => parseInt(id)),
                  updates: [
                    {
                      field: "visibility",
                      action: "set",
                      value: {
                        visibility: "show",
                        marketplace: selectedMarketplace,
                      },
                    },
                  ],
                });
              },
              confirmationMessage: (
                <p>
                  Are you sure you want to show {selected.length} {selected.length > 1 ? "brands" : "brand"}?
                </p>
              ),
            },
          ]}
        />
      </Wrap>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={"Search"}
              isExpandable
              className={css({ flex: "0 1 350px" })}
              value={table.getState().globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <Flex
          className={css({
            gap: "small",
            alignItems: "center",
            marginEnd: "xxlarge",
            marginBottom: "large",
            fontSize: "small",
            fontWeight: 500,
          })}
        >
          <Switch
            bsSize="small"
            onColor="#FFFFFF"
            onChange={() => setShowHiddenOrgs(!showHiddenOrgs)}
            value={showHiddenOrgs}
            onText={""}
            offText={""}
          />
          Show Hidden Brands
        </Flex>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        className={css({ minWidth: "100%" })}
        renderExpander={(row) => {
          return (
            <div className={css({ padding: "medium", backgroundColor: "rgb(239, 239, 239)" })}>
              <ExpanderContainer
                className={css({
                  maxWidth: "calc(100vw - 140px)!important",
                  overflow: "hidden",
                })}
              >
                <OrgPerformanceExpander row={{ _original: row.original }} allOrgs={false} />
              </ExpanderContainer>
            </div>
          );
        }}
      />
      <HStack width={"100%"} padding={"small"}>
        <DownloadV4Report downloadMutation={exportMutation} />
      </HStack>
    </div>
  );
};

export default AgencyOrgReportTable;
